import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import {
  ToggleOn as FlagIcon,
  Add as AddIcon,
  Settings as ManageIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { LoadingScreen } from '../../components/common/LoadingScreen';

interface FeatureFlag {
  id: number;
  name: string;
  description: string | null;
  enabled: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  organization_count: number;
  user_count: number;
}

export default function FeatureFlagsPage() {
  const navigate = useNavigate();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);

  // Create dialog state
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<FeatureFlag[]>('/admin/feature-flags');
      setFlags(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setNewName('');
    setNewDescription('');
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  const handleCreate = async () => {
    try {
      setCreateLoading(true);
      setCreateError(null);
      await apiClient.post('/admin/feature-flags', {
        name: newName,
        description: newDescription || null,
      });
      setCreateDialogOpen(false);
      await loadFlags();
    } catch (err: unknown) {
      setCreateError(extractErrorMessage(err, 'Failed to create feature flag'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteFlag = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedFlag) return;

    try {
      await apiClient.delete(`/admin/feature-flags/${selectedFlag.id}`);
      await loadFlags();
      setDeleteDialogOpen(false);
      setSelectedFlag(null);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to delete feature flag'));
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading feature flags..." />;
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            Error loading feature flags: {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FlagIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Feature Flags
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            size="small"
          >
            Create Flag
          </Button>
        </Box>

        {/* Flags Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Organisations</TableCell>
                <TableCell align="right">Users</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flags.map((flag) => (
                <TableRow
                  key={flag.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/feature-flags/${flag.id}`)}
                >
                  <TableCell>{flag.name}</TableCell>
                  <TableCell>{flag.description || '-'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={flag.enabled ? 'Enabled' : 'Disabled'}
                      color={flag.enabled ? 'success' : 'default'}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{flag.organization_count}</TableCell>
                  <TableCell align="right">{flag.user_count}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/feature-flags/${flag.id}`); }}
                      title="Manage flag"
                    >
                      <ManageIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeleteFlag(flag); }}
                      title="Delete flag"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {flags.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No feature flags yet
            </Typography>
          </Box>
        )}

        {/* Create Flag Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Feature Flag</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {createError && (
                <Alert severity="error" onClose={() => setCreateError(null)}>
                  {createError}
                </Alert>
              )}
              <TextField
                label="Flag Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={createLoading || !newName.trim()}
            >
              {createLoading ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Feature Flag"
          message={`Are you sure you want to delete "${selectedFlag?.name}"? This will also remove all grants. This action cannot be undone.`}
          confirmText="Delete"
          confirmColor="error"
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setDeleteDialogOpen(false);
            setSelectedFlag(null);
          }}
        />
      </Box>
    </Container>
  );
}
